import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  HStack,
  Table,
  VStack,
  Text,
  Box,
  Flex,
  Button,
  Badge,
  IconButton,
  Image,
} from "@chakra-ui/react";
import { getCorporateData, updateCorporateStatus } from "../Redux/Action";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";
import { getLocalStorageItem, notifySuccess, notifyError } from "../Utils/Helper";
import CorporateDocumentViewer from "../Components/custom/CorporateDocumentViewer";
import CorporateDetailsModal from "../Components/custom/CorporateDetailsModal";
import { FiEye, FiFile, FiInfo } from "react-icons/fi";

const CorporateKYC = () => {
  const location = useLocation();
  
  // Use dynamic title management
  useEffect(() => {
    if (location.pathname === "/corporate-kyc") {
      setDynamicTitle("Corporate KYC");
    } else if (location.pathname === "/corporate-users") {
      setDynamicTitle("Corporate Users");
    }
  }, [location.pathname]);

  const dispatch = useDispatch();
  const [corporateList, setCorporateList] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [refetch, setRefetch] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedCorporate, setSelectedCorporate] = useState(null);
  const whitelabelLocalStorage = getLocalStorageItem("whitelabelData");

  useEffect(() => {
    setIsDataLoading(true);
    let payload;
    
    // Determine status based on current route
    const status = location.pathname === "/corporate-kyc" ? "pending_verification" : "approved";
    
    if (whitelabelLocalStorage !== null) {
      let whiteLabelID = whitelabelLocalStorage._id;
      payload = {
        whiteLabelID,
        status,
      };
    } else {
      payload = {
        status,
      };
    }
    
    dispatch(
      getCorporateData({
        payload,
        callback: (data) => {
          if (data.meta.code === 200) {
            setCorporateList(data?.data || []);
            setIsDataLoading(false);
          } else {
            setIsDataLoading(false);
          }
        },
      })
    );
  }, [whitelabelLocalStorage?._id, refetch, location.pathname]);

  const handleUpdateStatus = (email, status) => {
    let payload;
    if (whitelabelLocalStorage !== null) {
      let whiteLabelID = whitelabelLocalStorage._id;
      payload = {
        email,
        status,
        whiteLabelID,
      };
    } else {
      payload = {
        email,
        status,
      };
    }
    
    dispatch(
      updateCorporateStatus({
        payload,
        callback: (data) => {
          if (data?.meta?.code === 200) {
            // Refresh the data after successful update
            setRefetch(!refetch);
            notifySuccess(data?.meta?.message || "Corporate status updated successfully");
          } else {
            notifyError(data?.meta?.message || "Failed to update corporate status");
          }
        },
      })
    );
  };

  const handlerefetchData = () => setRefetch(!refetch);

  const handleViewDocuments = (documents) => {
    setSelectedDocuments(documents || []);
    setIsDocumentViewerOpen(true);
  };

  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setSelectedDocuments([]);
  };

  const handleViewDetails = (corporateData) => {
    setSelectedCorporate(corporateData);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedCorporate(null);
  };

  return (
    <VStack spacing={4} align="stretch" width="100%">
      <HStack width="100%" justifyContent="flex-end">
        <Button
          size="sm"
          colorScheme="blue"
          onClick={handlerefetchData}
          isLoading={isDataLoading}
          loadingText="Refreshing"
        >
          Refresh
        </Button>
      </HStack>

      {isDataLoading ? (
        <Box
          height="200px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>Loading corporate KYC data...</Text>
        </Box>
      ) : (
        <Box
          overflow="auto"
          height="calc(100vh - 200px)"
          width="100%"
          borderRadius="md"
          bg="white"
          boxShadow="sm"
        >
          <Table.Root size="sm" variant="unstyled" width="100%">
            <Table.Header>
              <Table.Row 
                bg="bg.subtle"
                borderBottom="1px solid"
                borderColor="gray.300"
              >
                <Table.ColumnHeader width="15%" textAlign="left" py={3}>
                  Company Name
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left" py={3}>
                  Email
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left" py={3}>
                  Incorporation Type
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left" py={3}>
                  Country
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left" py={3}>
                  Incorporation Date
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left" py={3}>
                  Mobile
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left" py={3}>
                  Status
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left" py={3}>
                  Documents
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left" py={3}>
                  Actions
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {corporateList?.length > 0 ? (
                corporateList.map((item) => (
                  <Table.Row
                    key={item._id}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    _hover={{ bg: "gray.50" }}
                  >
                    <Table.Cell textAlign="left">
                      <Text fontWeight="medium">
                        {item?.companyInfo?.basic?.corporateName || "N/A"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <Text>{item.email || "N/A"}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <Text>{item?.companyInfo?.basic?.typeOfIncorporation || "N/A"}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <Text>{item?.companyInfo?.basic?.countryOfIncorporation || "N/A"}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <Text>
                        {item?.companyInfo?.basic?.incorporationDate 
                          ? new Date(item.companyInfo.basic.incorporationDate).toLocaleDateString()
                          : "N/A"
                        }
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <Text>{item?.companyInfo?.basic?.mobile || "N/A"}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <Badge
                        colorScheme={
                          item.status === "approved" 
                            ? "green" 
                            : item.status === "rejected" 
                            ? "red" 
                            : "orange"
                        }
                        variant="subtle"
                      >
                        {item.status || "pending_verification"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <HStack spacing={2}>
                        {item.documents && item.documents.length > 0 ? (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            leftIcon={<FiEye />}
                            onClick={() => handleViewDocuments(item.documents)}
                            variant="outline"
                          >
                            View ({item.documents.length})
                          </Button>
                        ) : (
                          <Text color="gray.500" fontSize="sm">No documents</Text>
                        )}
                      </HStack>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <VStack spacing={2} align="start">
                        {location.pathname === "/corporate-kyc" && (
                          <HStack spacing={2}>
                            <Button
                              size="xs"
                              colorScheme="green"
                              onClick={() => handleUpdateStatus(item.email, "approved")}
                              isLoading={isDataLoading}
                            >
                              Approve
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="red"
                              onClick={() => handleUpdateStatus(item.email, "rejected")}
                              isLoading={isDataLoading}
                            >
                              Reject
                            </Button>
                          </HStack>
                        )}
                        <Button
                          size="xs"
                          colorScheme="blue"
                          variant="outline"
                          leftIcon={<FiInfo />}
                          onClick={() => handleViewDetails(item)}
                        >
                          See Complete Details
                        </Button>
                      </VStack>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={9} textAlign="center" py={8}>
                    <Text color="gray.500">No corporate KYC data available</Text>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
      
      <CorporateDocumentViewer
        isOpen={isDocumentViewerOpen}
        onClose={handleCloseDocumentViewer}
        documents={selectedDocuments}
      />
      
      <CorporateDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        corporateData={selectedCorporate}
      />
    </VStack>
  );
};

export default CorporateKYC;